import React, { useCallback, useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { DocumentExtractionStatus, DocumentType, UserDocument } from "@/shared/types/documentTypes";
import { coalesceDocumentExtension } from "@/shared/utils/documentTypeUtils";
import { useAuth } from "@/shared/hooks/useAuth";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useInvalidateUserDocuments } from "@/shared/hooks/useUserDocuments";
import { useInvalidateEstatePlan } from "@/shared/hooks/useEstatePlan";
import { useDeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import { EventType } from "@/shared/constants/events";
import { trackEvent, trackJourneyEvent } from "@/shared/utils/core";
import { extractTextFromPdf } from "@/shared/utils/pdfTextExtraction";
import { DocumentLimitError } from "@/shared/utils/uploadDocument";

// Response from successful document storage
export interface StoreDocumentResponse {
  documentId: number;
  fileKey: string;
}

// Staged document in upload queue (File with additional metadata)
export interface StagedDocument extends File {
  previewUrl: string;
  documentType: DocumentType;
  extension: string;
  createdAt: Date;
  physicalLocation: string;
  contacts: string[];
  extractionStatus: string;
  remoteFileKey?: string;
}

// Result returned from successful document upload
export interface UploadResult {
  documentId: number;
  fileKey: string;
  fileName: string;
  name: string;
}

export interface DocumentFormInput {
  documents: UserDocument[];
  documentTypes: { documentType: DocumentType }[];
}

interface UseDocumentUploadProps {
  onComplete: (uploadResults?: UploadResult[]) => void;
  onFirstDocumentAdded?: () => void;
  assetId?: number;
  accountId?: number;
  deceasedUserId?: number;
  postLossTaskId?: number;
  documentTypes: { id: DocumentType; label: string }[];
  isUploadingInPostLossOnboarding?: boolean;
  skipInvalidation?: boolean;
  unlimited?: boolean;
  disableParsingAnimation?: boolean;
  stepLabel?: string;
  autoSaveCallback?: (documentIds: number[]) => void;
  existingDocumentIds?: number[];
}

console.log("[useDocumentUpload] MODULE LOADED");
const MAX_FILE_SIZE_BYTES = 200 * 1024 * 1024; // 200MB

async function computeFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

const SUPPORTED_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/heic",
  "image/heif",
];

const PARSEABLE_DOCUMENT_TYPES = [DocumentType.WILL, DocumentType.DEATH_CERTIFICATE];

import { convertImageFileToPdf } from "@/shared/utils/convertImageFileToPdf";

export default function useDocumentUpload({
  onComplete,
  onFirstDocumentAdded,
  deceasedUserId,
  postLossTaskId,
  documentTypes,
  skipInvalidation = false,
  unlimited = false,
  disableParsingAnimation = false,
  stepLabel,
  autoSaveCallback,
  existingDocumentIds = [],
}: UseDocumentUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [documents, setDocuments] = useState<StagedDocument[]>([]);
  const [succeeded, setSucceeded] = useState<StoreDocumentResponse[]>([]);
  const [failed, setFailed] = useState<string[]>([]);
  const [hitDocumentLimit, setHitDocumentLimit] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
  const [failedParsing, setFailedParsing] = useState<string[]>([]);
  const [errorIndexes, setErrorIndexes] = useState<number[]>([]);

  const queryClient = useQueryClient();
  const invalidateDocuments = useInvalidateUserDocuments();
  const invalidateFamilyDocuments = useInvalidateUserDocuments();
  const invalidateEstatePlan = useInvalidateEstatePlan();
  const { getAccessToken } = useAuth();
  const { user, person } = useAppContext();
  const deceasedUserData = useDeceasedUserData();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<DocumentFormInput>();

  const {
    fields: documentTypeFields,
    append: appendDocumentType,
    remove: handleDocumentTypeRemove,
  } = useFieldArray({
    name: "documentTypes",
    control,
    rules: {
      validate: (value) => value.length > 0,
    },
  });

  // Sync form state with documents state
  useEffect(() => {
    setValue("documents", documents as unknown as UserDocument[]);
  }, [documents, setValue]);

  // Auto-clear succeeded messages after 10s
  useEffect(() => {
    const timer = setTimeout(() => setSucceeded([]), 10000);
    return () => clearTimeout(timer);
  }, [succeeded]);

  // Auto-clear failed messages after 10s
  useEffect(() => {
    const timer = setTimeout(() => setFailed([]), 10000);
    return () => clearTimeout(timer);
  }, [failed]);

  // Auto-clear failed parsing messages after 10s
  useEffect(() => {
    const timer = setTimeout(() => setFailedParsing([]), 10000);
    return () => clearTimeout(timer);
  }, [failedParsing]);

  // Watch for document changes to enable/disable submit
  useEffect(() => {
    const { unsubscribe } = watch((data) => {
      const hasDocs = data?.documents && data.documents.length > 0;
      setSubmitDisabled(!hasDocs);
    });
    return () => unsubscribe();
  }, [watch]);

  const invalidate = useCallback(
    (deceasedUserId?: number) => {
      invalidateEstatePlan();
      invalidateDocuments();

      if (person?.estate_id) {
        queryClient.invalidateQueries({
          queryKey: ["documents", person.estate_id],
        });
        queryClient.invalidateQueries({
          queryKey: ["hasDeathCert", person.estate_id],
        });
        queryClient.invalidateQueries({
          queryKey: ["hasAuthorityDocument", person.estate_id],
        });
      }

      if (deceasedUserId) {
        invalidateFamilyDocuments();
      }
    },
    [
      invalidateEstatePlan,
      invalidateDocuments,
      invalidateFamilyDocuments,
      queryClient,
      person?.estate_id,
    ],
  );

  const handleDocumentAdd = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, documentType?: DocumentType) => {
      if (documents.length === 0 && onFirstDocumentAdded) {
        onFirstDocumentAdded();
      }
      trackEvent(EventType.ADD_DOCUMENT);

      const newDocs = Array.from(event.target.files as FileList);
      const unsupportedFiles = newDocs.filter((doc) => !SUPPORTED_FILE_TYPES.includes(doc.type));
      if (unsupportedFiles.length > 0) {
        setFailed((prev) => [...prev, "Unsupported file type. Please upload a PDF or image file."]);
        return;
      }

      // Limit documents based on remaining slots (unless unlimited mode)
      let docsToAdd: File[];
      if (!unlimited && documentTypes.length > 0) {
        const remainingSlots = documentTypes.length - documents.length;
        if (remainingSlots <= 0) return;
        docsToAdd = newDocs.slice(0, remainingSlots);
      } else {
        docsToAdd = newDocs;
      }

      const defaultDocType =
        documentTypes?.length === 1 ? documentTypes[0].id : documentType || ("" as DocumentType);

      const newDocumentsWithMetadata = docsToAdd.map((doc) => {
        return Object.assign(doc, {
          previewUrl: URL.createObjectURL(doc),
          documentType: defaultDocType as DocumentType,
          extension: coalesceDocumentExtension(doc.type),
          createdAt: new Date(),
          physicalLocation: "",
          contacts: [],
          extractionStatus: DocumentExtractionStatus.NoExtraction,
        });
      });

      newDocumentsWithMetadata.forEach(() => {
        appendDocumentType({ documentType: defaultDocType as DocumentType });
      });

      setDocuments((prev) => [...prev, ...newDocumentsWithMetadata]);
    },
    [appendDocumentType, documents.length, documentTypes, onFirstDocumentAdded, unlimited],
  );

  const handleDocumentTypeChange = useCallback(
    (v: DocumentType, ix: number) => {
      trackEvent(EventType.CHANGE_DOCUMENT_TYPE, { documentType: v });
      const newDocuments = [...documents];
      newDocuments[ix].documentType = v;
      setDocuments(newDocuments);
    },
    [documents],
  );

  const handleDocumentRemove = useCallback((ix: number) => {
    trackEvent(EventType.REMOVE_DOCUMENT);
    setDocuments((prev) => [...prev.slice(0, ix), ...prev.slice(ix + 1)]);
  }, []);

  // Stage already-uploaded remote documents (from mobile session)
  const stageRemoteDocument = useCallback(
    (remote: {
      fileKey: string;
      fileName: string;
      contentType?: string;
      documentType?: DocumentType;
    }) => {
      const defaultDocType =
        documentTypes?.length === 1
          ? documentTypes[0].id
          : (remote.documentType as DocumentType) || ("" as DocumentType);

      const staged = {
        name: remote.fileName,
        type: remote.contentType || "application/pdf",
        previewUrl: "",
        documentType: defaultDocType,
        extension: coalesceDocumentExtension(remote.contentType || "application/pdf"),
        createdAt: new Date(),
        physicalLocation: "",
        contacts: [],
        extractionStatus: DocumentExtractionStatus.NoExtraction,
        remoteFileKey: remote.fileKey,
        // File interface stubs (not used for remote docs)
        size: 0,
        lastModified: Date.now(),
        webkitRelativePath: "",
        arrayBuffer: () => Promise.resolve(new ArrayBuffer(0)),
        bytes: () => Promise.resolve(new Uint8Array()),
        slice: () => new Blob(),
        stream: () => new ReadableStream(),
        text: () => Promise.resolve(""),
      } as StagedDocument;

      appendDocumentType({ documentType: defaultDocType as DocumentType });
      setDocuments((prev) => [...prev, staged]);
    },
    [appendDocumentType, documentTypes],
  );

  const clearDocuments = useCallback(() => {
    setDocuments([]);
  }, []);

  const onSubmit = useCallback(async () => {
    console.log("[upload] onSubmit called, documents:", documents.length);
    // Validate document types if required
    const requireDocumentType = documentTypes.length > 0 && !unlimited;
    if (requireDocumentType && documents.some((doc) => !doc.documentType)) {
      const errorIndexTemp: number[] = [];
      documents.forEach((doc, ix) => {
        if (!doc.documentType) errorIndexTemp.push(ix);
      });
      setErrorIndexes(errorIndexTemp);
      return;
    }
    setErrorIndexes([]);
    setHitDocumentLimit(false);

    trackEvent(EventType.UPLOAD_DOCUMENTS);
    if (documents.length === 0) {
      onComplete();
      return;
    }

    setSubmitDisabled(true);
    const accessToken = await getAccessToken?.();
    if (!accessToken) {
      throw new Error("No access token found");
    }

    setUploading(true);

    const uploadPromises = documents.map(async (document) => {
      console.log("[upload] starting upload for:", document.name);
      let fileForUpload: File = document as unknown as File;
      let uploadFileName: string = document.name;
      let contentType: string = (document as unknown as File).type;

      // Convert images to PDF
      if (contentType?.startsWith("image/")) {
        const converted = await convertImageFileToPdf(fileForUpload);
        fileForUpload = converted;
        uploadFileName = converted.name;
        contentType = "application/pdf";
      }

      // Extract text client-side for text-based PDFs (skips Reducto OCR on server)
      let extractedText: string | null | undefined;
      if (contentType === "application/pdf") {
        try {
          extractedText = await extractTextFromPdf(fileForUpload);
        } catch {
          // Password-protected or corrupted PDF — Reducto will handle OCR server-side
        }
      }

      // Skip presign + upload for remotely uploaded docs
      let fileKey: string | undefined = document.remoteFileKey;
      let duplicateId: number | null = null;
      let fileHash: string | undefined;

      if (!fileKey) {
        if (!deceasedUserId) {
          throw new Error("Person ID is required for document upload");
        }

        // File size check
        if (fileForUpload.size > MAX_FILE_SIZE_BYTES) {
          throw new Error(
            `File too large (${Math.round(fileForUpload.size / 1024 / 1024)}MB). Maximum size is 200MB.`,
          );
        }

        // Deduplication check
        fileHash = await computeFileHash(fileForUpload);
        console.log("[upload] file_hash:", fileHash, "file_size:", fileForUpload.size);

        const dupResponse = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/${deceasedUserId}/check-duplicate`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              file_hash: fileHash,
              file_size: fileForUpload.size,
              estate_id: deceasedUserId,
            }),
          },
        );

        if (dupResponse.ok) {
          const dupData = await dupResponse.json();
          console.log("[upload] dedup response:", dupData);
          if (dupData.is_duplicate) {
            duplicateId = dupData.duplicate_id;
          }
        } else {
          console.warn("[upload] dedup check failed:", dupResponse.status);
        }

        // Skip S3 upload if duplicate
        if (!duplicateId) {
          // Get presigned URL
          const presignedResponse = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/${deceasedUserId}/presigned-url`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
                apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
              },
              body: JSON.stringify({
                file_name: uploadFileName,
                file_type: contentType || "application/octet-stream",
                file_size: fileForUpload.size || 0,
              }),
            },
          );

          if (!presignedResponse.ok) {
            if (presignedResponse.status === 403) {
              throw new DocumentLimitError();
            }
            throw new Error(`Failed to get presigned URL: ${presignedResponse.status}`);
          }

          const presignedData = await presignedResponse.json();
          const { upload_url, file_key } = presignedData;
          fileKey = file_key;

          // Upload file to S3
          const uploadResponse = await fetch(upload_url, {
            method: "PUT",
            body: fileForUpload,
            headers: { "Content-Type": contentType || "application/octet-stream" },
          });

          if (!uploadResponse.ok) {
            const errorText = await uploadResponse.text();
            throw new Error(`Failed to upload file to S3: ${uploadResponse.status} ${errorText}`);
          }
        }
      }

      // Save metadata to Supabase
      const foundKey = Object.keys(DocumentType).find(
        (key) => DocumentType[key as keyof typeof DocumentType] === document.documentType,
      );
      const documentTypeKey = foundKey
        ? DocumentType[foundKey as keyof typeof DocumentType]
        : document.documentType;

      if (!deceasedUserId) {
        throw new Error("Person ID is required for saving document metadata");
      }

      if (!fileKey) {
        throw new Error("File key is required for saving document metadata");
      }

      const saveData = {
        file_key: fileKey,
        file_name: uploadFileName,
        file_type: contentType || "application/octet-stream",
        file_size: fileForUpload.size || 0,
        document_type: documentTypeKey || "other",
        extracted_text: extractedText,
        file_hash: fileHash,
        ...(duplicateId && { duplicate_id: duplicateId }),
      };
      console.log("[upload] saveData:", {
        ...saveData,
        extracted_text: saveData.extracted_text
          ? `${saveData.extracted_text.length} chars`
          : undefined,
      });

      const saveResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/${deceasedUserId}/metadata`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify(saveData),
        },
      );

      if (!saveResponse.ok) {
        if (saveResponse.status === 403) {
          throw new DocumentLimitError();
        }
        const errorText = await saveResponse.text();
        throw new Error(`Failed to save document metadata: ${saveResponse.status} ${errorText}`);
      }

      const saveResponseData = await saveResponse.json();
      const documentId =
        saveResponseData.document?.id ||
        saveResponseData.documentId ||
        saveResponseData.id ||
        saveResponseData.data?.id ||
        saveResponseData.data?.document_id ||
        saveResponseData.document_id;

      if (!documentId) {
        throw new Error("Document upload succeeded but no document ID was returned");
      }
      if (!fileKey) {
        throw new Error("Document upload succeeded but no file key was returned");
      }

      return {
        documentId: Number(documentId),
        fileKey,
        fileName: uploadFileName,
        name: uploadFileName,
      } satisfies UploadResult;
    });

    const response = await Promise.allSettled(uploadPromises);

    const successfulUploads = response
      .filter(
        (result): result is PromiseFulfilledResult<UploadResult> => result.status === "fulfilled",
      )
      .map((result) => result.value);

    const failedUploads = response.filter(
      (result): result is PromiseRejectedResult => result.status === "rejected",
    );

    if (successfulUploads.length > 0) {
      if (onFirstDocumentAdded) {
        trackJourneyEvent(EventType.FIRST_DOCUMENT_UPLOADED, {
          document_type: documents[0]?.documentType,
          upload_method: "drag_drop",
          document_count: documents.length,
          from_task: !!postLossTaskId,
        });
      }

      trackJourneyEvent(EventType.DOCUMENT_VAULT_ACCESSED, {
        action: "upload_success",
        document_types: documents.map((doc) => doc.documentType).filter(Boolean),
        upload_count: successfulUploads.length,
        failed_count: failedUploads.length,
      });

      successfulUploads.forEach((upload, index) => {
        trackEvent(EventType.DOCUMENT_UPLOADED, {
          document_type: documents[index]?.documentType || "unknown",
          upload_source: postLossTaskId ? "task_wizard" : "document_vault",
          file_name: upload.fileName || upload.name,
        });
      });

      trackEvent(EventType.UPLOAD_DOCUMENTS_SUCCESS, {
        document_types: documents.map((doc) => doc.documentType).filter(Boolean),
      });

      const newSucceededEntries: StoreDocumentResponse[] = successfulUploads.map((res) => ({
        documentId: res.documentId,
        fileKey: res.fileKey,
      }));

      setSucceeded((prev) => [...prev, ...newSucceededEntries]);

      if (autoSaveCallback && stepLabel) {
        const newDocumentIds = successfulUploads
          .map((res) => res.documentId)
          .filter((id): id is number => id !== undefined && id !== null && !isNaN(id));

        if (newDocumentIds.length > 0) {
          autoSaveCallback([...existingDocumentIds, ...newDocumentIds]);
        }
      }
    }

    if (failedUploads.length > 0) {
      trackEvent(EventType.UPLOAD_DOCUMENTS_FAILED, {
        document_types: documents.map((doc) => doc.documentType).filter(Boolean),
      });
      const limitError = failedUploads.find((r) => r.reason instanceof DocumentLimitError);
      if (limitError) {
        setFailed((prev) => [...prev, (limitError.reason as Error).message]);
        setHitDocumentLimit(true);
      } else {
        setFailed((prev) => [...prev, "Upload failed. Please try again."]);
      }
    }

    setUploading(false);

    // Show parsing animation for parseable document types
    if (!disableParsingAnimation) {
      const responsesNeedingExtraction = successfulUploads.filter((res) =>
        PARSEABLE_DOCUMENT_TYPES.some((docType) => res.fileKey?.includes(docType)),
      );
      if (responsesNeedingExtraction.length > 0) {
        setParsing(true);
      }
    }

    setSubmitDisabled(false);

    // Delay completion to allow UI feedback, then invalidate queries
    setTimeout(() => {
      if (!skipInvalidation) {
        invalidate(deceasedUserId);
      }
      onComplete(successfulUploads.length > 0 ? successfulUploads : []);

      // Poll for AI document type inference updates
      if (!skipInvalidation && person?.estate_id) {
        const invalidateAfterParse = () => {
          queryClient.invalidateQueries({ queryKey: ["documents", person.estate_id] });
          queryClient.invalidateQueries({ queryKey: ["userEstates"] });
        };
        setTimeout(invalidateAfterParse, 10000);
        setTimeout(invalidateAfterParse, 20000);
        setTimeout(invalidateAfterParse, 30000);
        setTimeout(invalidateAfterParse, 45000);
      }
    }, 5000);
  }, [
    documents,
    documentTypes.length,
    unlimited,
    getAccessToken,
    onComplete,
    postLossTaskId,
    deceasedUserId,
    invalidate,
    skipInvalidation,
    autoSaveCallback,
    stepLabel,
    existingDocumentIds,
    user?.user_id,
    user?.full_name,
    disableParsingAnimation,
    deceasedUserData,
    person?.full_name,
    person?.estate_id,
    onFirstDocumentAdded,
    queryClient,
  ]);

  return {
    handleSubmit,
    onSubmit,
    submitDisabled,
    register,
    handleDocumentAdd,
    stageRemoteDocument,
    uploading,
    failed,
    succeeded,
    failedParsing,
    documents,
    errors,
    errorIndexes,
    handleDocumentTypeChange,
    handleDocumentRemove,
    handleDocumentTypeRemove,
    documentTypeFields,
    parsing,
    setParsing,
    clearDocuments,
    hitDocumentLimit,
  };
}
